import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import {useAuth} from "react-oidc-context";
import {onIdTokenChanged, signInWithCustomToken, signOut} from "firebase/auth";
import axios from "axios";
import {auth as firebaseAuth, projectId} from "../config/firebase-config";

/**
 * Identitätstausch: Aus dem ZITADEL-ID-Token wird über die Token-Route des
 * Mailservers eine Firebase-Identität mit dem Nutzerkreis als Claim `rolle`.
 * Erst mit ihr lässt Firestore interne Daten lesen.
 *
 * Zustand:
 * - `anonym`: bei ZITADEL nicht angemeldet.
 * - `tauschend`: angemeldet, aber Firebase kennt (noch) nicht diesen Nutzer.
 * - `bereit`: Firebase meldet eine Identität, deren `uid` dem ZITADEL-`sub`
 *   entspricht.
 * - `fehler`: der Tausch ist gescheitert, `fehler` sagt warum.
 *
 * Fehlerfälle:
 * - 403: kein Zugriff, kein Retry.
 * - 400 (unbekanntes Projekt), 503 (Mailserver unvollständig eingerichtet) und
 *   andere 4xx: Administrator informieren, kein automatischer Retry. Ebenso,
 *   wenn Firebase das Custom Token ablehnt.
 * - Netz, Timeout, übrige 5xx: Backoff 2 s, 4 s, 8 s … höchstens 60 s, ohne
 *   Obergrenze der Versuche.
 * - Fehlende Token-URL: Betriebsfehler, es wird gar nicht erst getauscht.
 * - Ist `auth_time` der passenden Identität älter als 24 Stunden, behandelt
 *   Firestore sie wie anonym. Ohne erfolgreichen neuen Tausch heißt das:
 *   Sitzung abgelaufen.
 */
const FirebaseIdentitaetContext = createContext(null);

export const ZUSTAND_ANONYM = "anonym";
export const ZUSTAND_TAUSCHEND = "tauschend";
export const ZUSTAND_BEREIT = "bereit";
export const ZUSTAND_FEHLER = "fehler";

export const FEHLER_KEIN_ZUGRIFF = "keinZugriff";
export const FEHLER_MAILSERVER = "mailserver";
export const FEHLER_NETZ = "netz";
export const FEHLER_TOKEN_URL_FEHLT = "tokenUrlFehlt";
export const FEHLER_SITZUNG_ABGELAUFEN = "sitzungAbgelaufen";

// Wie lange Firestore eine Identität nach dem Tausch als frisch gilt.
const FRISCH_MS = 24 * 60 * 60 * 1000;
const TIMEOUT_MS = 20 * 1000;
const BACKOFF_START_MS = 2 * 1000;
const BACKOFF_MAX_MS = 60 * 1000;

const backoffMs = (fehlversuche) =>
  Math.min(BACKOFF_START_MS * 2 ** (fehlversuche - 1), BACKOFF_MAX_MS);

const fehlerArt = (error) => {
  const status = error.response?.status;
  if (status === 403) {
    return FEHLER_KEIN_ZUGRIFF;
  }
  // Lehnt Firebase das Custom Token ab, hilft Warten so wenig wie bei 400.
  if (
    error.code?.startsWith("auth/") &&
    error.code !== "auth/network-request-failed"
  ) {
    return FEHLER_MAILSERVER;
  }
  // 503 heißt bei der Token-Route: Konfiguration fehlt. Warten hilft da nicht.
  if (!status || (status >= 500 && status !== 503)) {
    return FEHLER_NETZ;
  }
  return FEHLER_MAILSERVER;
};

const tauschen = async (tokenUrl, idToken) => {
  const antwort = await axios.post(
    tokenUrl,
    {idToken, projectId},
    {timeout: TIMEOUT_MS},
  );
  return antwort.data.token;
};

export const FirebaseIdentitaetProvider = ({children}) => {
  const oidc = useAuth();
  // undefined: Firebase hat die gespeicherte Identität noch nicht geprüft.
  const [identitaet, setIdentitaet] = useState(undefined);
  const [fehler, setFehler] = useState(null);
  // Läuft gerade ein Tausch, der noch nicht gescheitert ist?
  const [laeuft, setLaeuft] = useState(false);
  // Jede Erhöhung startet einen neuen Tausch mit demselben ID-Token.
  const [versuch, setVersuch] = useState(0);
  const fehlversuche = useRef(0);
  // Ein gelungener Tausch macht frisch, auch bevor Firebase die neue
  // auth_time meldet. Gilt nur für den Nutzer, für den getauscht wurde.
  const [getauscht, setGetauscht] = useState(null);
  // Anmeldungen laufen nacheinander: Eine ältere, schon gestartete darf eine
  // neuere nicht überholen und deren Claim überschreiben.
  const anmeldungen = useRef(Promise.resolve());

  const idToken = oidc.user?.id_token;
  const sub = oidc.user?.profile?.sub;
  const oidcAbgemeldet = !oidc.isLoading && !oidc.isAuthenticated;
  const tokenUrl = process.env.REACT_APP_FIREBASE_TOKEN_URL;

  // onIdTokenChanged statt onAuthStateChanged: Ein erneuter Tausch für
  // dieselbe uid ändert nur das Token — und mit ihm womöglich die Rolle.
  useEffect(() => {
    let aktuell = 0;
    return onIdTokenChanged(firebaseAuth, async (user) => {
      const nr = ++aktuell;
      if (!user) {
        setIdentitaet(null);
        return;
      }
      let rolle = null;
      let authTime = null;
      try {
        ({
          claims: {rolle = null, auth_time: authTime = null},
        } = await user.getIdTokenResult());
      } catch (error) {
        // Ohne lesbaren Claim keine Rolle — also kein interner Bereich.
        console.error("Firebase-Token nicht lesbar", error);
      }
      if (nr === aktuell) {
        setIdentitaet({uid: user.uid, rolle, authTime});
      }
    });
  }, []);

  // Ein neues ID-Token beginnt von vorn: ohne alten Fehler, ohne Backoff.
  useEffect(() => {
    fehlversuche.current = 0;
    setFehler(null);
  }, [idToken]);

  // Ein Auslöser: jedes neue ID-Token (Login, Start mit ZITADEL-Sitzung, Silent
  // Renew) — dazu Wiederholungen. Kommt ein neueres, wird der laufende Tausch
  // verworfen. Bei automatischen Wiederholungen bleibt der Fehler stehen, bis
  // ein Versuch gelingt, damit der Hinweis nicht flackert.
  useEffect(() => {
    if (!idToken) {
      setLaeuft(false);
      return;
    }
    if (!tokenUrl) {
      setLaeuft(false);
      setFehler(FEHLER_TOKEN_URL_FEHLT);
      return;
    }
    let verworfen = false;
    let wiederholung;
    setLaeuft(true);
    tauschen(tokenUrl, idToken)
      .then((customToken) => {
        const anmeldung = anmeldungen.current.then(() => {
          if (!verworfen) {
            return signInWithCustomToken(firebaseAuth, customToken);
          }
        });
        anmeldungen.current = anmeldung.catch(() => {});
        return anmeldung;
      })
      .then(() => {
        if (!verworfen) {
          fehlversuche.current = 0;
          setGetauscht({sub, um: Date.now()});
          setFehler(null);
          setLaeuft(false);
        }
      })
      .catch((error) => {
        if (verworfen) {
          return;
        }
        setLaeuft(false);
        const art = fehlerArt(error);
        if (art === FEHLER_KEIN_ZUGRIFF) {
          // Kein Retry: Ohne App-Rolle hilft auch ein zweiter Versuch nicht.
          setFehler(FEHLER_KEIN_ZUGRIFF);
          signOut(firebaseAuth).catch(() => {});
          return;
        }
        console.error("Identitätstausch gescheitert", error);
        setFehler(art);
        if (art === FEHLER_NETZ) {
          fehlversuche.current += 1;
          wiederholung = setTimeout(
            () => setVersuch((v) => v + 1),
            backoffMs(fehlversuche.current),
          );
        }
      });
    return () => {
      verworfen = true;
      clearTimeout(wiederholung);
    };
    // sub gehört zum idToken und ändert sich nur mit ihm.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken, tokenUrl, versuch]);

  // Sofort neu tauschen, ein laufender Backoff wird damit verworfen und
  // beginnt danach wieder bei 2 s.
  const erneutVersuchen = useCallback(() => {
    fehlversuche.current = 0;
    setFehler(null);
    setLaeuft(true);
    setVersuch((v) => v + 1);
  }, []);

  // Die 24-Stunden-Grenze wird beim Rendern geprüft; der Timer sorgt nur
  // dafür, dass genau an der Grenze neu gerendert wird.
  const authTime = identitaet?.authTime ?? null;
  const frischSeit = Math.max(
    authTime === null ? 0 : authTime * 1000,
    getauscht && getauscht.sub === sub ? getauscht.um : 0,
  );
  const grenze = authTime === null ? null : frischSeit + FRISCH_MS;
  const abgelaufen = grenze !== null && Date.now() >= grenze;
  const [, grenzeErreicht] = useReducer((n) => n + 1, 0);
  useEffect(() => {
    if (grenze === null) {
      return;
    }
    const timer = setTimeout(grenzeErreicht, Math.max(0, grenze - Date.now()));
    return () => clearTimeout(timer);
  }, [grenze]);

  // Ohne ZITADEL-Sitzung keine Firebase-Identität, und keine fremde: Beim
  // Nutzerwechsel im selben Browser darf die Identität des Vorgängers nicht
  // weiterleben, auch nicht, solange der Tausch läuft oder scheitert.
  const firebaseUid = identitaet?.uid ?? null;
  const fremd = Boolean(sub) && firebaseUid !== null && firebaseUid !== sub;
  useEffect(() => {
    if (oidcAbgemeldet) {
      setFehler(null);
    }
    if ((oidcAbgemeldet && firebaseUid) || fremd) {
      signOut(firebaseAuth).catch((error) => {
        console.error("Firebase-Abmeldung gescheitert", error);
      });
    }
  }, [oidcAbgemeldet, firebaseUid, fremd]);

  const abmelden = useCallback(async () => {
    try {
      await signOut(firebaseAuth);
    } catch (error) {
      console.error("Firebase-Abmeldung gescheitert", error);
    }
    await oidc.signoutRedirect();
  }, [oidc]);

  const passend = Boolean(sub) && identitaet?.uid === sub;
  let zustand;
  let angezeigterFehler = null;
  if (!oidc.isAuthenticated) {
    zustand = ZUSTAND_ANONYM;
  } else if (
    fehler === FEHLER_KEIN_ZUGRIFF ||
    fehler === FEHLER_TOKEN_URL_FEHLT
  ) {
    zustand = ZUSTAND_FEHLER;
    angezeigterFehler = fehler;
  } else if (passend && !abgelaufen) {
    // Scheitert ein Tausch im Hintergrund, bleibt die passende Identität
    // nutzbar.
    zustand = ZUSTAND_BEREIT;
  } else if (passend && (fehler || !laeuft)) {
    // Abgelaufen, und kein Tausch ist unterwegs, der das noch richten könnte.
    // Stille Wiederholungen laufen weiter.
    zustand = ZUSTAND_FEHLER;
    angezeigterFehler = FEHLER_SITZUNG_ABGELAUFEN;
  } else if (fehler) {
    zustand = ZUSTAND_FEHLER;
    angezeigterFehler = fehler;
  } else {
    zustand = ZUSTAND_TAUSCHEND;
  }

  const wert = useMemo(
    () => ({
      zustand,
      fehler: angezeigterFehler,
      // Nur die Rolle der passenden Identität zählt, nie die eines Vorgängers.
      rolle: zustand === ZUSTAND_BEREIT ? identitaet.rolle : null,
      abmelden,
      erneutVersuchen,
    }),
    [zustand, angezeigterFehler, identitaet, abmelden, erneutVersuchen],
  );

  return (
    <FirebaseIdentitaetContext.Provider value={wert}>
      {children}
    </FirebaseIdentitaetContext.Provider>
  );
};

export const useFirebaseIdentitaet = () => {
  const wert = useContext(FirebaseIdentitaetContext);
  if (!wert) {
    throw new Error(
      "useFirebaseIdentitaet braucht einen FirebaseIdentitaetProvider",
    );
  }
  return wert;
};
